<script setup lang="ts">
import { computed } from "vue"

/**
 * ActivityBadge
 *
 * The default activity indicator badge display.  This component should be used for basic indications
 * that activity has taken place. This may include providing a :value="true" to display an empty
 * but visible colored badge.  Commonly this will include a numeric value to indicate a count.
 *
 * NOTE(spk): The max prop is only used when the value prop is of type "number" as a basic convenience
 * for numeric based displays.  Generally, the display of the value is left up to the caller to ensure
 * it is both useful, accurate, and does not cause layout issues.
 *
 * <template>
 *   <ActivityBadge
 *     :value="5"
 *     : max="999"
 *   />
 * </template>
 */

const props = withDefaults(
  defineProps<{
    max?: number
    value: string | number | boolean
  }>(),
  {
    max: 99,
  }
)

const indicatorStr = computed(() => {
  if (typeof props.value === "number" && props.value > props.max) {
    return `${props.max}+`
  }

  return ["string", "number"].includes(typeof props.value)
    ? props.value.toString()
    : ""
})
</script>

<template>
  <div
    v-if="props.value"
    class="inline-block rounded-full bg-red-500 min-h-3.5 min-w-3.5 px-1.5 py-0.5 text-xs/none font-bold text-white text-center shadow"
  >
    {{ indicatorStr }}
  </div>
</template>
